import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Upload, X, AlertCircle, FileText, Image, File } from 'lucide-react';

// Objeto para mapear tipos MIME a nombres amigables
const FRIENDLY_TYPE_NAMES = {
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'image/jpeg': 'Imagen',
  'image/png': 'Imagen',
  'image/gif': 'Imagen',
};

/**
 * Componente FileUploader que soporta múltiples archivos y mantiene compatibilidad con formData.
 * Acepta todos los tipos de archivo por defecto si `allowedTypes` no se especifica.
 * * @param {Object} props - Propiedades del componente.
 * @param {Object} props.formData - Objeto formData que contiene los archivos.
 * @param {Function} props.setFormData - Función para actualizar el estado de formData.
 * @param {string} [props.fieldName='hojadevida'] - Nombre del campo en formData.
 * @param {boolean} [props.isUploading=false] - Deshabilita interacciones durante la carga.
 * @param {boolean} [props.multiple=false] - Permite la carga de múltiples archivos.
 * @param {string[]} [props.allowedTypes=[]] - Array de tipos MIME permitidos. Un array vacío permite cualquier tipo.
 * @param {number} [props.maxSize=10485760] - Tamaño máximo por archivo en bytes (10MB por defecto).
 * @param {string} [props.label='archivo'] - Etiqueta para describir el tipo de archivo esperado.
 * @returns {JSX.Element}
 */
const FileUploader = ({ 
  formData, 
  setFormData, 
  fieldName = 'hojadevida',
  isUploading = false, 
  multiple = false,
  allowedTypes = [], // El cambio clave: por defecto es un array vacío
  maxSize = 10 * 1024 * 1024, // 10MB
  label = "archivo"
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    // Inicializa el campo en formData si es múltiple y no existe
    if (multiple && !Array.isArray(formData[fieldName])) {
      setFormData(prev => ({...prev, [fieldName]: []}));
    }
  }, [fieldName, multiple, setFormData]);

  // Derivamos los archivos del estado `formData` para mantenerlo como única fuente de verdad
  const files = useMemo(() => {
    const fieldData = formData[fieldName];
    if (multiple) {
      return Array.isArray(fieldData) ? fieldData : [];
    }
    return fieldData ? [fieldData] : [];
  }, [formData, fieldName, multiple]);
  
  const hasFiles = files.length > 0;

  // Manejadores de eventos de arrastre
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUploading) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Validar un único archivo
  const validateFile = (file) => {
    // 1. Validar tamaño
    if (file.size > maxSize) {
      return `El archivo ${file.name} excede el tamaño máximo de ${(maxSize / (1024 * 1024)).toFixed(0)}MB.`;
    }
    
    // 2. Validar tipo (solo si se especifican tipos permitidos)
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      return `El tipo de archivo de ${file.name} no es válido.`;
    }
    
    return null; // El archivo es válido
  };

  // Procesar una lista de archivos
  const processFiles = (fileList) => {
    const newErrors = [];
    const validFiles = [];
    
    Array.from(fileList).forEach(file => {
      const error = validateFile(file);
      if (error) {
        newErrors.push(error);
      } else {
        validFiles.push(file);
      }
    });
    
    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors([]); // Limpiar errores si todos los archivos son válidos
    
    setFormData(prev => {
      if (multiple) {
        // Añadir nuevos archivos a los existentes
        const currentFiles = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
        return { ...prev, [fieldName]: [...currentFiles, ...validFiles] };
      } else {
        // Reemplazar el archivo existente
        return { ...prev, [fieldName]: validFiles[0] };
      }
    });
  };

  // Manejar soltado de archivos
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUploading) return;
    
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  // Manejar selección desde el input
  const handleChange = (e) => {
    e.preventDefault();
    if (isUploading) return;
    
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = ''; // Resetear el input para permitir seleccionar el mismo archivo de nuevo
    }
  };

  // Activar clic en el input
  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  // Eliminar un archivo
  const removeFile = (indexToRemove) => {
    if (isUploading) return;
    setErrors([]);
    setFormData(prev => {
      if (multiple) {
        const updatedFiles = files.filter((_, index) => index !== indexToRemove);
        return { ...prev, [fieldName]: updatedFiles };
      } else {
        const newFormData = { ...prev };
        delete newFormData[fieldName];
        return newFormData;
      }
    });
  };

  // Obtener ícono según el tipo de archivo
  const getFileIcon = (file) => {
    if (!file?.type) return <File size={20} className="text-gray-500 flex-shrink-0" />;
    
    if (file.type.startsWith('image/')) return <Image size={20} className="text-purple-500 flex-shrink-0" />;
    if (file.type === 'application/pdf') return <FileText size={20} className="text-red-500 flex-shrink-0" />;
    if (file.type.includes('word')) return <FileText size={20} className="text-blue-500 flex-shrink-0" />;
    
    return <File size={20} className="text-gray-500 flex-shrink-0" />;
  };
  
  // Generar texto descriptivo de los tipos aceptados
  const getAllowedTypesText = () => {
    if (allowedTypes.length === 0) return null; // No mostrar nada si se aceptan todos
    
    const uniqueNames = [...new Set(allowedTypes.map(type => FRIENDLY_TYPE_NAMES[type] || type.split('/')[1]))];
    return `Formatos: ${uniqueNames.join(', ')}`;
  };

  return (
    <div className="w-full font-sans">
      {/* Errores de validación */}
      {errors.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded">
          <div className="flex items-center">
            <AlertCircle size={20} className="text-red-500 mr-2 flex-shrink-0" />
            <h3 className="text-red-800 font-semibold">Error al cargar</h3>
          </div>
          <ul className="list-disc pl-8 mt-1 space-y-1">
            {errors.map((error, index) => (
              <li key={index} className="text-red-700 text-sm">{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Zona de carga (se oculta si no es múltiple y ya hay un archivo) */}
      {(!hasFiles || multiple) && (
        <div 
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200
            ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'} 
            ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleButtonClick}
        >
          <div className="flex flex-col items-center justify-center">
            <Upload size={36} className={`${dragActive ? 'text-blue-600' : 'text-gray-400'} mb-2`} />
            <p className="text-sm text-gray-600 mb-1">
              Arrastra y suelta {multiple ? "tus" : "tu"} {label} o{' '}
              <span className="font-semibold text-blue-600">búscalo en tu equipo</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {getAllowedTypesText()} (máx. {(maxSize / (1024 * 1024)).toFixed(0)}MB)
            </p>
          </div>
          
          <input
            ref={inputRef}
            type="file"
            accept={allowedTypes.join(',')}
            onChange={handleChange}
            className="hidden"
            disabled={isUploading}
            multiple={multiple}
          />
        </div>
      )}

      {/* Lista de archivos cargados */}
      {hasFiles && (
        <div className={`border rounded-lg p-3 ${(!hasFiles || multiple) ? 'mt-4' : ''}`}>
          <h4 className="font-semibold text-sm mb-2 text-gray-700">
            {multiple 
              ? `${files.length} ${label}${files.length !== 1 ? 's' : ''} cargado${files.length !== 1 ? 's' : ''}`
              : `${label.charAt(0).toUpperCase() + label.slice(1)} cargado`}
          </h4>
          
          <div className="space-y-2">
            {files.map((file, index) => (
              <div key={file.name + index} className="flex items-center justify-between bg-gray-50 rounded-md p-2 hover:bg-gray-100">
                <div className="flex items-center min-w-0 mr-2">
                  {getFileIcon(file)}
                  <span className="ml-2 text-sm text-gray-800 truncate" title={file.name}>
                    {file.name}
                  </span>
                </div>
                <div className="flex items-center flex-shrink-0">
                  <span className="text-xs text-gray-500 mr-3">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                  <button 
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-gray-500 hover:text-red-500 disabled:opacity-50"
                    disabled={isUploading}
                    aria-label={`Eliminar ${file.name}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;